import { Injectable, inject } from "@angular/core";
import { AccountType } from "../../types/AccountType";
import { OwnerType } from "../../types/OwnerType";
import { MutationType, RobService } from "@services/rob/rob.service";
import { WebLogger } from "@weps/shared-http";

@Injectable({
  providedIn: "root",
})
export class QueryBuilderService {
  private readonly robService = inject(RobService);
  ACCOUNT_ALIAS = "accountMutation";
  OWNER_ALIAS = "ownerMutation";

  AccountTypeToMutationTypeMap: Record<AccountType, MutationType> = {
    [AccountType.BANK]: MutationType.ACCOUNT,
    [AccountType.RETIREMENT_ANNUITY]: MutationType.ACCOUNT,
    [AccountType.INVESTMENT]: MutationType.ACCOUNT,
    [AccountType.REAL_ESTATE]: MutationType.ASSET,
    [AccountType.MORTGAGE]: MutationType.ASSET,
    [AccountType.LIABILITIES]: MutationType.LIABILITY,
    [AccountType.TRUST]: MutationType.TRUST,
  };

  /**
   * Build owner queries if owner information has changed
   * @param ownerType - The type of owner from OwnerType enum
   */
  buildOwnerQueries(ownerType: string): any[] {
    WebLogger.log({
      message: "QueryBuilderService building owner queries",
      payload: { ownerType },
    });

    const ownerQueries: any[] = [];

    if (ownerType === OwnerType.TRUST_OWNER) {
      WebLogger.log({
        message: "QueryBuilderService building trust mutation query for TRUST_OWNER",
      });
      try {
        // Use the existing trust mutation logic from RobService
        const trustQuery = this.robService.buildOwnerQueries(MutationType.TRUST);
        ownerQueries.push(trustQuery);
      } catch (error) {
        WebLogger.log({
          message: "QueryBuilderService trust query build error",
          level: "error",
          payload: {
            error:
              error instanceof Error
                ? { message: error.message, stack: error.stack }
                : String(error),
          },
        });
        // Continue without trust query - don't fail the entire save process
      }
    }

    // For other owner types, no additional owner queries are needed
    // as they don't require separate trust/owner entity creation

    return ownerQueries;
  }

  /**
   * Build the account/asset/liability mutation query
   */
  buildAccountQuery(accountType: AccountType): any {
    WebLogger.log({
      message: "QueryBuilderService building account query",
      payload: { accountType },
    });

    // Map AccountType to MutationType using the existing mapping from ROB service
    const mutationType = this.AccountTypeToMutationTypeMap[accountType];
    return this.robService.buildMeoaMutationQuery(mutationType);
  }

  /**
   * Combine multiple GraphQL mutations into a single request using aliases
   */
  combineQueriesWithAliases(ownerQueries: any[], accountQuery: any): any {
    const mutations: string[] = [];

    // Add owner mutations with aliases (if any) TODO: disable for now and implement once we we're saving
    ownerQueries.forEach((ownerQuery, index) => {
      if (ownerQuery && ownerQuery.query) {
        const ownerMutation = this.extractMutationBody(ownerQuery.query);
        mutations.push(`${this.OWNER_ALIAS}${index}: ${ownerMutation}`);
      }
    });

    // Add account mutation with alias
    if (accountQuery && accountQuery.query) {
      const accountMutation = this.extractMutationBody(accountQuery.query);
      mutations.push(`${this.ACCOUNT_ALIAS}: ${accountMutation}`);
    }

    // Combine all mutations into a single GraphQL request
    const combinedQuery = `mutation {
      ${mutations.join("\n      ")}
    }`;

    WebLogger.log({
      message: "QueryBuilderService combined GraphQL mutation created",
      payload: { combinedQuery },
    });

    return {
      query: combinedQuery,
    };
  }

  /**
   * Extract the mutation body from a GraphQL mutation string
   */
  private extractMutationBody(mutationQuery: string): string {
    // Remove the outer "mutation {" and "}" wrapper
    const match = mutationQuery.match(/mutation\s*{([\s\S]*)}$/);
    return match ? match[1].trim() : mutationQuery;
  }
}
